import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    CardMedia,
    CardContent,
    CardActions,
    Typography,
    IconButton,
    Button,
    Box,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import {
    ShoppingCart as ShoppingCartIcon,
    Share as ShareIcon,
    LocalOffer as LocalOfferIcon,
    Remove as RemoveIcon,
    Add as AddIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import cartService from '../services/cartService';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';



const FALLBACK_SM = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjUwIiBoZWlnaHQ9IjI1MCIgdmlld0JveD0iMCAwIDI1MCAyNTAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyNTAiIGhlaWdodD0iMjUwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik0xMjUgNzVWMTc1TTc1IDEyNUgxNzUiIHN0cm9rZT0iIzk5OTk5OSIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiLz4KPHN2Zz4K';
const FALLBACK_LG = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgdmlld0JveD0iMCAwIDQwMCA0MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iNDAwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik0yMDAgMTIwVjI4ME0xMjAgMjAwSDI4MCIgc3Ryb2tlPSIjOTk5OTk5IiBzdHJva2Utd2lkdGg9IjMiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIvPgo8L3N2Zz4K';

const ProductCard = ({ product }) => {

    const [showDetails, setShowDetails] = useState(false);
    const [isAddingToCart, setIsAddingToCart] = useState(false);
    // selectedSizes maps Size -> quantity, e.g. { L: 2, M: 1 }
    const [selectedSizes, setSelectedSizes] = useState({});
    const { refreshCart } = useCart();
    const { user } = useAuth();
    const navigate = useNavigate();

    const TAG_BADGE_STYLES = {
        Bestseller: { color: '#ffd700', textColor: '#1a1a1a' },
        Trending: { color: '#4fc3f7', textColor: '#08262e' },
        Featured: { color: '#ba68c8', textColor: '#210b28' },
        'Limited Edition': { color: '#ff8a65', textColor: '#2b0f04' }
    };


    const handleShare = async (e) => {
        e.stopPropagation(); // don't trigger any card-level navigation
        const url = new URL(`${window.location.origin}/products/${product.ProductCode}`);
        if (user?.tokenNo) {
            url.searchParams.set('ref', user.tokenNo);
        }
        const shareUrl = url.toString();

        if (navigator.share) {
            try {
                await navigator.share({ title: product.ProductName, url: shareUrl });
            } catch (err) { /* user cancelled — no-op */ }
        } else {
            await navigator.clipboard.writeText(shareUrl);
            toast.success('Product link copied to clipboard');
        }
    };

    const getAvailableSizes = () => {
        if (!product.SizeDetails || product.SizeDetails.length === 0) return [];
        return product.SizeDetails.filter(s => s.QuantityInStock > 0);
    };

    const availableSizes = getAvailableSizes();
    const hasSizes = availableSizes.length > 0;

    const getStockForSize = (size) => {
        const match = product.SizeDetails?.find(s => s.Size === size);
        return match ? match.QuantityInStock : 0;
    };

    // Clicking a chip toggles it in/out of selection (starts at qty 1)
    const toggleSize = (size, e) => {
        e.stopPropagation();
        setSelectedSizes(prev => {
            const next = { ...prev };
            if (next[size]) {
                delete next[size];
            } else {
                next[size] = 1;
            }
            return next;
        });
    };

    // Adjusts quantity for an already-selected size, capped by its stock and a floor of 1
    const updateQty = (size, delta, e) => {
        e.stopPropagation();
        setSelectedSizes(prev => {
            const stock = getStockForSize(size);
            const current = prev[size] || 1;
            const nextQty = Math.min(stock, Math.max(1, current + delta));
            return { ...prev, [size]: nextQty };
        });
    };

    const totalSelectedQty = Object.values(selectedSizes).reduce((sum, q) => sum + q, 0);

    const handleAddToCartClick = async () => {
        if (isAddingToCart) return;

        if (hasSizes && Object.keys(selectedSizes).length === 0) {
            toast.error('Please select at least one size');
            return;
        }

        setIsAddingToCart(true);
        try {
            if (hasSizes) {
                const entries = Object.entries(selectedSizes);
                for (const [size, qty] of entries) {
                    await cartService.addToCart(product.ProductCode, qty, size);
                }
                const summary = entries.map(([size, qty]) => `${size} x${qty}`).join(', ');
                toast.success(`Added to cart (${summary})`);
                setSelectedSizes({});
            } else {
                await cartService.addToCart(product.ProductCode, 1, null);
                toast.success('Added to cart');
            }
            refreshCart();
        } catch (error) {
            toast.error(error.message || 'Failed to add to cart');
        } finally {
            setIsAddingToCart(false);
        }
    };

    const formatPrice = (price) =>
        new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(price);

    const getStockStatus = () => {
        if (product.Category === 'Customized') return { status: 'available', text: 'Made to Order' };
        const stock = product.TotalSizeStock || product.QuantityInStock || 0;
        if (stock > 10) return { status: 'in-stock', text: 'In Stock' };
        if (stock > 0) return { status: 'low-stock', text: `Only ${stock} left` };
        return { status: 'out-of-stock', text: 'Out of Stock' };
    };

    const getStockChipStyles = () => {
        const stock = product.TotalSizeStock || product.QuantityInStock || 0;
        if (product.Category === 'Customized') {
            return { color: '#bc965e', borderColor: 'rgba(188, 150, 94, 0.5)', bgcolor: 'rgba(188, 150, 94, 0.12)' };
        }
        if (stock > 10) {
            return { color: '#4caf50', borderColor: 'rgba(76, 175, 80, 0.5)', bgcolor: 'rgba(76, 175, 80, 0.12)' };
        }
        if (stock > 0) {
            return { color: '#ffb74d', borderColor: 'rgba(255, 183, 77, 0.5)', bgcolor: 'rgba(255, 183, 77, 0.12)' };
        }
        return { color: '#ff6b6b', borderColor: 'rgba(255, 107, 107, 0.5)', bgcolor: 'rgba(255, 107, 107, 0.12)' };
    };

    const stockStatus = getStockStatus();
    const stockChipStyles = getStockChipStyles();
    const API_BASE_URL = process.env.REACT_APP_API_URL.replace('/api', '');
    const photoUrl = product.Photo ? `${API_BASE_URL}${product.Photo}` : FALLBACK_SM;

    // Renders the size chips as toggleable multi-select controls.
    // Shared between the card and the details modal.
    const renderSizeChips = () => (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 0.5 }}>
            {product.SizeDetails.map((s, i) => {
                const inStock = s.QuantityInStock > 0;
                const isSelected = Boolean(selectedSizes[s.Size]);
                return (
                    <Chip
                        key={i}
                        label={`${s.Size} (${s.QuantityInStock})`}
                        size="small"
                        clickable={inStock}
                        onClick={inStock ? (e) => toggleSize(s.Size, e) : undefined}
                        variant={isSelected ? 'filled' : 'outlined'}
                        sx={{
                            height: 24,
                            fontSize: '0.65rem',
                            fontWeight: isSelected ? 700 : 400,
                            color: !inStock
                                ? 'rgba(255,255,255,0.4)'
                                : isSelected ? '#000' : '#bc965e',
                            backgroundColor: isSelected ? '#bc965e' : 'transparent',
                            borderColor: inStock ? 'rgba(188, 150, 94, 0.4)' : 'rgba(255,255,255,0.2)',
                            opacity: inStock ? 1 : 0.6,
                            cursor: inStock ? 'pointer' : 'default',
                            '&:hover': inStock ? {
                                backgroundColor: isSelected ? '#a67c4a' : 'rgba(188, 150, 94, 0.15)'
                            } : undefined
                        }}
                    />
                );
            })}
        </Box>
    );

    // Renders a compact quantity stepper row for each currently-selected size
    const renderQtyControls = () => {
        const entries = Object.entries(selectedSizes);
        if (entries.length === 0) return null;
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.75 }}>
                {entries.map(([size, qty]) => {
                    const stock = getStockForSize(size);
                    return (
                        <Box
                            key={size}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                px: 1,
                                py: 0.25,
                                borderRadius: 1.5,
                                border: '1px solid rgba(188, 150, 94, 0.3)',
                                bgcolor: 'rgba(188, 150, 94, 0.08)'
                            }}
                        >
                            <Typography variant="caption" sx={{ color: '#bc965e', fontWeight: 700 }}>
                                {size}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <IconButton
                                    size="small"
                                    onClick={(e) => updateQty(size, -1, e)}
                                    disabled={qty <= 1}
                                    sx={{ color: '#bc965e', p: 0.25, '&.Mui-disabled': { color: 'rgba(255,255,255,0.2)' } }}
                                >
                                    <RemoveIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                                <Typography variant="caption" sx={{ color: 'white', minWidth: 16, textAlign: 'center' }}>
                                    {qty}
                                </Typography>
                                <IconButton
                                    size="small"
                                    onClick={(e) => updateQty(size, 1, e)}
                                    disabled={qty >= stock}
                                    sx={{ color: '#bc965e', p: 0.25, '&.Mui-disabled': { color: 'rgba(255,255,255,0.2)' } }}
                                >
                                    <AddIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        );
    };

    return (
        <Card
            elevation={0}
            className="glass-card"
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                maxWidth: 320,
                width: '100%',
                mx: 'auto',
                borderRadius: 3,
                border: '1px solid rgba(188, 150, 94, 0.2)',
                background: 'rgba(20, 20, 20, 0.6)',
                backdropFilter: 'blur(10px)',
                transition: 'transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease',
                overflow: 'hidden',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 24px rgba(188, 150, 94, 0.15)',
                    borderColor: 'rgba(188, 150, 94, 0.4)'
                },
            }}
        >
            {/* ── Image ── */}
            <Box sx={{ position: 'relative' }}>
                <CardMedia
                    component="img"
                    height="180"
                    image={photoUrl}
                    alt={product.ProductName}
                    sx={{ objectFit: 'cover', backgroundColor: '#0f0f0f' }}
                    onError={(e) => { if (e.target.src !== FALLBACK_SM) e.target.src = FALLBACK_SM; }}
                />
                {product.Category === 'Customized' && (
                    <Chip
                        icon={<LocalOfferIcon sx={{ fontSize: 14 }} />}
                        label={product.Category}
                        size="small"
                        sx={{
                            position: 'absolute',
                            top: 8,
                            left: 8,
                            fontWeight: 700,
                            fontSize: '0.65rem',
                            color: '#bc965e',
                            border: '1px solid rgba(188, 150, 94, 0.4)',
                            bgcolor: 'rgba(188, 150, 94, 0.12)'
                        }}
                    />
                )}

                {/* Product tag badge - only renders when a valid tag is set */}
                {product.ProductTag && TAG_BADGE_STYLES[product.ProductTag] && (
                    <Chip
                        label={product.ProductTag.toUpperCase()}
                        size="small"
                        sx={{
                            position: 'absolute',
                            top: product.Category === 'Customized' ? 34 : 8,
                            left: 8,
                            fontWeight: 700,
                            fontSize: '0.6rem',
                            letterSpacing: '0.02em',
                            backgroundColor: `${TAG_BADGE_STYLES[product.ProductTag].color}e6`,
                            color: TAG_BADGE_STYLES[product.ProductTag].textColor,
                            backdropFilter: 'blur(4px)'
                        }}
                    />
                )}
                <Chip
                    label={stockStatus.text.toUpperCase()}
                    size="small"
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        fontWeight: 700,
                        fontSize: '0.65rem',
                        letterSpacing: '0.03em',
                        border: `1px solid ${stockChipStyles.borderColor}`,
                        color: stockChipStyles.color,
                        bgcolor: stockChipStyles.bgcolor,
                        backdropFilter: 'blur(4px)'
                    }}
                />

                {/* Share icon - bottom right, subtle hover reveal */}
                <IconButton
                    onClick={handleShare}
                    size="small"
                    title="Share product"
                    sx={{
                        position: 'absolute',
                        bottom: 8,
                        right: 8,
                        bgcolor: 'rgba(0, 0, 0, 0.5)',
                        color: '#bc965e',
                        backdropFilter: 'blur(4px)',
                        opacity: 0,
                        transition: 'opacity 0.25s ease',
                        '.MuiCard-root:hover &': { opacity: 1 },
                        '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' }
                    }}
                >
                    <ShareIcon fontSize="small" />
                </IconButton>
            </Box>

            {/* ── Content ── */}
            <CardContent sx={{ flexGrow: 1, textAlign: 'center', py: 1.5, px: 2 }}>
                <Typography
                    variant="caption"
                    sx={{
                        color: '#bc965e',
                        fontWeight: 700,
                        letterSpacing: '0.08em',
                        fontSize: '0.65rem'
                    }}
                >
                    {(product.ProductTypeName || product.ProductType || '').toUpperCase()}
                </Typography>

                <Typography
                    variant="h6"
                    component="h3"
                    className="glass-text-primary"
                    sx={{ fontWeight: 700, mt: 0.25, mb: 0.5, fontSize: '0.95rem', lineHeight: 1.2 }}
                >
                    {product.ProductName}
                </Typography>

                <Typography
                    variant="h6"
                    sx={{ color: '#bc965e', fontWeight: 800, mb: 0.5, fontSize: '1.15rem' }}
                >
                    {formatPrice(product.Price)}
                </Typography>

                {product.Color && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 0.5 }}>
                        <Typography variant="caption" className="glass-text-secondary">Color:</Typography>
                        <Typography variant="caption" fontWeight={600} className="glass-text-primary">{product.Color}</Typography>
                    </Box>
                )}

                {product.Description && (
                    <Typography
                        variant="caption"
                        className="glass-text-secondary"
                        sx={{
                            mb: 0.5,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                        }}
                    >
                        {product.Description}
                    </Typography>
                )}

                {product.SizeDetails && product.SizeDetails.length > 0 && (
                    <Box sx={{ mb: 0.5 }}>
                        {renderSizeChips()}
                        {renderQtyControls()}
                    </Box>
                )}

                {(!product.SizeDetails || product.SizeDetails.length === 0) && product.AvailableSizes && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
                        <Typography variant="caption" className="glass-text-secondary">Sizes:</Typography>
                        <Typography variant="caption" fontWeight={600} className="glass-text-primary">{product.AvailableSizes}</Typography>
                    </Box>
                )}
            </CardContent>

            {/* ── Actions ── */}
            <CardActions sx={{ p: 1.5, pt: 0, gap: 1 }}>
                <Button
                    size="small"
                    fullWidth
                    onClick={() => navigate(`/products/${product.ProductCode}`)}
                    sx={{
                        color: '#bc965e',
                        border: '1px solid rgba(188, 150, 94, 0.4)',
                        borderRadius: 2,
                        textTransform: 'uppercase',
                        fontWeight: 700,
                        fontSize: '0.6rem',
                        py: 0.6,
                        px: 1,
                        minHeight: 32,
                        whiteSpace: 'nowrap',
                        '&:hover': {
                            backgroundColor: 'rgba(188, 150, 94, 0.1)',
                            borderColor: '#bc965e'
                        }
                    }}
                >
                    Quick View
                </Button>
                <Button
                    variant="contained"
                    size="small"
                    fullWidth
                    onClick={handleAddToCartClick}
                    disabled={stockStatus.status === 'out-of-stock' || isAddingToCart || (hasSizes && Object.keys(selectedSizes).length === 0)}
                    startIcon={<ShoppingCartIcon sx={{ fontSize: 13 }} />}
                    sx={{
                        backgroundColor: '#bc965e',
                        color: '#000',
                        borderRadius: 2,
                        textTransform: 'uppercase',
                        fontWeight: 700,
                        fontSize: '0.6rem',
                        py: 0.6,
                        px: 2,
                        minHeight: 32,
                        whiteSpace: 'nowrap',
                        boxShadow: 'none',
                        '& .MuiButton-startIcon': { marginRight: 0.5 },
                        '&:hover': { backgroundColor: '#a67c4a', boxShadow: 'none' },
                        '&.Mui-disabled': {
                            backgroundColor: 'rgba(188, 150, 94, 0.2)',
                            color: 'rgba(255,255,255,0.4)'
                        }
                    }}
                >
                    {isAddingToCart
                        ? 'Adding...'
                        : stockStatus.status === 'out-of-stock'
                            ? 'Out'
                            : hasSizes && totalSelectedQty > 0
                                ? `Add (${totalSelectedQty})`
                                : 'Add to Cart'}
                </Button>
            </CardActions>

            {/* ── Product Details Modal ── */}
            <Dialog
                open={showDetails}
                onClose={() => setShowDetails(false)}
                maxWidth="md"
                fullWidth
                slotProps={{
                    paper: {
                        sx: {
                            background: 'rgba(30, 30, 30, 0.97)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(188, 150, 94, 0.2)',
                            borderRadius: 2
                        }
                    }
                }}
            >
                <DialogTitle>
                    <Typography variant="h5" component="div" sx={{ color: 'white', fontWeight: 700 }}>
                        {product.ProductName}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
                        <Box sx={{ flex: '0 0 45%', maxWidth: { md: '45%' } }}>
                            <Box
                                component="img"
                                src={product.Photo ? `${API_BASE_URL}${product.Photo}` : FALLBACK_LG}
                                alt={product.ProductName}
                                sx={{
                                    width: '100%',
                                    height: 'auto',
                                    borderRadius: 1,
                                    backgroundColor: '#0f0f0f',
                                    display: 'block'
                                }}
                                onError={(e) => {
                                    if (e.target.src !== FALLBACK_LG) {
                                        e.target.src = FALLBACK_LG;
                                    }
                                }}
                            />
                        </Box>
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>Category</Typography>
                                <Typography variant="body1" fontWeight={600} sx={{ color: 'white' }}>{product.Category}</Typography>
                            </Box>
                            {product.ProductTag && TAG_BADGE_STYLES[product.ProductTag] && (
                                <Box>
                                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>Tag</Typography>
                                    <Chip
                                        label={product.ProductTag}
                                        size="small"
                                        sx={{
                                            mt: 0.5,
                                            fontWeight: 700,
                                            backgroundColor: TAG_BADGE_STYLES[product.ProductTag].color,
                                            color: TAG_BADGE_STYLES[product.ProductTag].textColor
                                        }}
                                    />
                                </Box>
                            )}
                            <Box>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>Type</Typography>
                                <Typography variant="body1" fontWeight={600} sx={{ color: 'white' }}>
                                    {product.ProductTypeName || product.ProductType}
                                </Typography>
                            </Box>
                            {product.Color && (
                                <Box>
                                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>Color</Typography>
                                    <Typography variant="body1" fontWeight={600} sx={{ color: 'white' }}>{product.Color}</Typography>
                                </Box>
                            )}
                            <Box>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>Price</Typography>
                                <Typography variant="h5" sx={{ color: '#bc965e', fontWeight: 800 }}>{formatPrice(product.Price)}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>Availability</Typography>
                                <Chip
                                    label={stockStatus.text}
                                    size="small"
                                    sx={{
                                        mt: 0.5,
                                        border: `1px solid ${stockChipStyles.borderColor}`,
                                        color: stockChipStyles.color,
                                        bgcolor: stockChipStyles.bgcolor
                                    }}
                                />
                            </Box>
                            {product.SizeDetails && product.SizeDetails.length > 0 && (
                                <Box>
                                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }} gutterBottom>
                                        Select Size(s) & Quantity
                                    </Typography>
                                    {renderSizeChips()}
                                    {renderQtyControls()}
                                </Box>
                            )}
                            {product.Description && (
                                <Box>
                                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }} gutterBottom>
                                        Description
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: 'white' }}>{product.Description}</Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: { xs: 2, sm: 3 }, pt: 1, flexWrap: 'wrap', gap: 1 }}>

                    <Button
                        variant="contained"
                        onClick={handleAddToCartClick}
                        disabled={stockStatus.status === 'out-of-stock' || isAddingToCart || (hasSizes && Object.keys(selectedSizes).length === 0)}
                        startIcon={<ShoppingCartIcon />}
                        sx={{
                            backgroundColor: '#bc965e',
                            color: '#000',
                            '&:hover': { backgroundColor: '#a67c4a' },
                            '&.Mui-disabled': {
                                backgroundColor: 'rgba(188, 150, 94, 0.2)',
                                color: 'rgba(255,255,255,0.4)'
                            }
                        }}
                    >
                        {isAddingToCart
                            ? 'Adding...'
                            : stockStatus.status === 'out-of-stock'
                                ? 'Out of Stock'
                                : hasSizes && totalSelectedQty > 0
                                    ? `Add to Cart (${totalSelectedQty})`
                                    : 'Add to Cart'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Card>
    );
};

export default ProductCard;